// CreateJob.form.controller.jsx
import React, { useState, useCallback } from 'react'
import SideViewModal from '../../../../../components/modals/SideViewModal/SideViewModal.component'
import AddJob from '../../AddJob.form'
import { useMutation } from '@apollo/client'
import { CREATE_JOB } from '../../../../../graphql/job/job.mutations'
import { GET_ALL_JOBS_WITH_FULL_DETAIL } from '../../../../../graphql/job/job.queries'
import { validate } from '../../../../../shared/utility'

// MUI feedback
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'

const validationSchema = {
  name: { type: 'input', validation: { required: true } },
  street1: { type: 'input', validation: {} },
  street2: { type: 'input', validation: {} },
  city: { type: 'input', validation: {} },
  state: { type: 'input', validation: {} },
  zipcode: { type: 'input', validation: {} },
  customerName: { type: 'input', validation: {} },
  customerContactName: { type: 'input', validation: {} },
  customerPhone: { type: 'input', validation: {} },
  customerEmail: { type: 'input', validation: {} },
  estimatedStartDate: { type: 'date', validation: { isDate: true } },
}

const MAX = {
  name: 100,
  street1: 255,
  street2: 255,
  city: 100,
  state: 100,
  zipcode: 20,
  customerName: 100,
  customerContactName: 100,
  customerPhone: 36,
  customerEmail: 254,
}

function normalizeSubmitData(formData) {
  const out = { ...formData }
  for (const k of Object.keys(out)) {
    const v = out[k]
    if (v && typeof v === 'object' && 'value' in v && Object.keys(v).length <= 3) out[k] = v.value
    if (typeof out[k] === 'string') {
      out[k] = out[k].trim()
      if (MAX[k]) out[k] = out[k].slice(0, MAX[k])
    }
  }
  if (out.estimatedStartDate?.format) {
    out.estimatedStartDate = out.estimatedStartDate.format('YYYY-MM-DD')
  }
  return out
}

function extractApolloError(err) {
  const msgs = []
  if (err?.graphQLErrors?.length) {
    for (const ge of err.graphQLErrors) {
      if (ge?.message) msgs.push(ge.message)
      const det = ge?.extensions?.exception?.detail || ge?.extensions?.detail
      if (det) msgs.push(det)
      const fieldErrors = ge?.extensions?.exception?.errors
      if (fieldErrors && typeof fieldErrors === 'object') {
        for (const [f, arr] of Object.entries(fieldErrors)) {
          msgs.push(`${f}: ${(Array.isArray(arr) ? arr.join(', ') : String(arr))}`)
        }
      }
    }
  }
  if (err?.networkError?.result?.errors?.length) {
    for (const e of err.networkError.result.errors) if (e?.message) msgs.push(e.message)
  }
  if (!msgs.length && err?.networkError?.message) msgs.push(err.networkError.message)
  if (!msgs.length && err?.message) msgs.push(err.message)
  return msgs.join('\n')
}

export default function CreateJob({ visible, closeForm }) {
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [toastOpen, setToastOpen] = useState(false)
  const [toastMsg, setToastMsg] = useState('')

  const [createJob, { loading }] = useMutation(CREATE_JOB, {
    refetchQueries: [{ query: GET_ALL_JOBS_WITH_FULL_DETAIL }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setIsSubmitting(false)
      setErrors({})
      closeForm()
    },
    onError: (e) => {
      const msg = extractApolloError(e) || 'Something went wrong.'
      console.error(e)
      setIsSubmitting(false)
      setErrors((prev) => ({ ...prev, __server: msg }))
      setToastMsg(msg)
      setToastOpen(true)
    },
  })

  const handleSubmit = useCallback(
    (formData) => {
      if (isSubmitting || loading) return
      const { isValid, errors: vErrors } = validate(formData, validationSchema)
      if (!isValid) return setErrors(vErrors)

      const variables = normalizeSubmitData(formData)
      // Helpful: quick check for long fields
      // console.table(Object.entries(variables).map(([k,v]) => [k, typeof v, String(v ?? '').length]))
      setIsSubmitting(true)
      createJob({ variables })
    },
    [isSubmitting, loading, createJob]
  )

  if (!visible) return null

  const addVariants = [
    { key: 'add-job', name: '', component: <AddJob />, stages: 1 },
  ]

  return (
    <>
      <SideViewModal
        show={visible}
        submitText={isSubmitting || loading ? 'Saving…' : 'Add Job'}
        onClose={() => { setErrors({}); setIsSubmitting(false); closeForm() }}
        onSubmit={handleSubmit}
        title="New Job"
        variants={addVariants}
        errors={errors}                    // field errors + __server available to modal
        submitDisabled={isSubmitting || loading}
      />

      {/* Visible server error */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="error" variant="filled" sx={{ width: '100%' }}>
          {toastMsg}
        </Alert>
      </Snackbar>
    </>
  )
}
